import { useEffect, useState } from "react";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Download, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const kg = (v) => `${Number(v || 0).toLocaleString("id-ID", { maximumFractionDigits: 2 })}`;
const tone = (v) => (Number(v) < 0 ? "text-rose-700" : "text-emerald-700");

/** Laba per produk arang (Penjualan tanpa pajak − HPP barang terjual). Ikut filter tanggal Semua Laporan. */
export default function LabaProdukTable({ start, end, reloadKey }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/laporan/laba-produk", { params: { start: start || undefined, end: end || undefined } })
      .then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const download = async (fmt) => {
    const url = new URL(`${API_BASE}/export/laba-produk`);
    url.searchParams.set("fmt", fmt);
    if (start) url.searchParams.set("start", start);
    if (end) url.searchParams.set("end", end);
    const res = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem("khn_token")}` } });
    if (!res.ok) { toast.error("Gagal mengunduh laporan"); return; }
    const a = document.createElement("a");
    a.href = URL.createObjectURL(await res.blob());
    a.download = `laba-per-produk.${fmt === "pdf" ? "pdf" : "xlsx"}`; a.click();
  };

  const t = d?.total;
  return (
    <div className="card-flat overflow-hidden" data-testid="laba-produk-card">
      <div className="p-4 md:p-5 flex flex-wrap items-start justify-between gap-3 border-b border-slate-100">
        <div>
          <h3 className="text-base md:text-lg font-semibold text-slate-900">Laba per Produk</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Laba Kotor = Penjualan (tanpa pajak) − HPP barang terjual (Kg terjual × HPP rata-rata produk).
            {(start || end) ? ` Periode: ${start || "awal"} s.d. ${end || "sekarang"}.` : " Semua periode."}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => download("pdf")} className="rounded-sm" data-testid="laba-produk-pdf"><Download size={14} className="mr-1" /> PDF</Button>
          <Button variant="outline" size="sm" onClick={() => download("xlsx")} className="rounded-sm" data-testid="laba-produk-xlsx"><FileSpreadsheet size={14} className="mr-1" /> Excel</Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs md:text-sm" data-testid="laba-produk-table">
          <thead><tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] uppercase tracking-wide text-slate-500">
            <th className="p-3">Produk</th><th className="p-3 text-right">Kg Terjual</th><th className="p-3 text-right">Penjualan</th>
            <th className="p-3 text-right">HPP/Kg</th><th className="p-3 text-right">HPP Terjual</th>
            <th className="p-3 text-right">Laba Kotor</th><th className="p-3 text-right">Margin</th>
          </tr></thead>
          <tbody>
            {!d && <tr><td colSpan="7" className="text-center py-8 text-slate-400">Memuat...</td></tr>}
            {d?.rows.map((r) => (
              <tr key={r.produk_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`laba-produk-row-${r.produk_id}`}>
                <td className="p-3 font-medium text-slate-800">{r.nama}<div className="text-[11px] text-slate-400 font-normal">{r.jumlah_transaksi} transaksi · produksi {kg(r.produksi_kg)} Kg</div></td>
                <td className="p-3 text-right font-mono">{kg(r.terjual_kg)}</td>
                <td className="p-3 text-right font-mono">{rupiah(r.penjualan)}</td>
                <td className="p-3 text-right font-mono text-slate-600">{rupiah(r.hpp_per_kg)}</td>
                <td className="p-3 text-right font-mono text-rose-700">{rupiah(r.hpp_terjual)}</td>
                <td className={`p-3 text-right font-mono font-semibold ${tone(r.laba_kotor)}`}>{rupiah(r.laba_kotor)}</td>
                <td className={`p-3 text-right font-mono ${tone(r.margin_persen)}`}>{Number(r.margin_persen || 0).toFixed(1)}%</td>
              </tr>
            ))}
            {t && (
              <tr className="bg-slate-50 font-semibold" data-testid="laba-produk-total">
                <td className="p-3">Total</td>
                <td className="p-3 text-right font-mono">{kg(t.terjual_kg)}</td>
                <td className="p-3 text-right font-mono">{rupiah(t.penjualan)}</td>
                <td className="p-3 text-right font-mono">-</td>
                <td className="p-3 text-right font-mono text-rose-700">{rupiah(t.hpp_terjual)}</td>
                <td className={`p-3 text-right font-mono ${tone(t.laba_kotor)}`}>{rupiah(t.laba_kotor)}</td>
                <td className={`p-3 text-right font-mono ${tone(t.margin_persen)}`}>{Number(t.margin_persen || 0).toFixed(1)}%</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
